import React, { useRef } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import type { Product } from '../../types/product';
import { easeEnter, stagger } from '../../utils/motion';
type ProductHeroProps = {
  product: Product;
};
export function ProductHero({
  product
}: ProductHeroProps) {
  const ref = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const panelY = useTransform(scrollYProgress, [0, 1], [0, 72]);
  return <section ref={ref} className={`${product.theme.surface} ${product.theme.onSurface} px-5 pb-20 pt-32 md:px-10 md:pb-28 md:pt-40 lg:pl-20`} aria-labelledby="product-hero-heading" data-id="element-735" data-mp="40n3i03wmqht">
      <div className="mx-auto grid max-w-shell gap-12 lg:grid-cols-[1.25fr_0.75fr] lg:items-center" data-id="element-736" data-mp="2omootdz5xpvs">
        <div data-id="element-737" data-mp="3hz0ya7tqhc5p">
          <motion.p initial={reduceMotion ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeEnter
        }} className={`text-xs font-extrabold tracking-[0.16em] ${product.theme.eyebrow}`} data-id="element-738" data-mp="3hzaex4sw26yq">
            {product.heroEyebrow}
          </motion.p>

          <h1 id="product-hero-heading" className="mt-6 font-display text-[clamp(3rem,7.5vw,6.5rem)] font-bold leading-[0.86]" data-id="element-739" data-mp="333bac44zmoyc">
            {product.heroTitle.map((line, index) => <motion.span key={line} initial={reduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: easeEnter,
            delay: stagger(index + 1, reduceMotion)
          }} className="block" data-id="element-740" data-mp="2u7286hpmlc8i" data-mpi={index}>
                {line}
              </motion.span>)}
          </h1>

          <motion.div initial={reduceMotion ? false : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeEnter,
          delay: stagger(4, reduceMotion)
        }} className="mt-10 max-w-xl" data-id="element-741" data-mp="2k4zpc2qdzhew">
            <p className={`text-lg leading-relaxed md:text-xl ${product.theme.onSurfaceMuted}`} data-id="element-742" data-mp="trim2gzrhpyu">
              {product.heroCopy}
            </p>
            <p className="mt-6 text-sm font-extrabold" data-id="element-743" data-mp="3aeqopsscbxlm">{product.outcome}</p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-744" data-mp="3fev39d9oui97">
              <Link to="/#start" className="inline-flex min-h-14 items-center justify-center gap-2 rounded-full bg-signal px-7 text-sm font-extrabold text-white transition-colors hover:bg-signal-dark active:bg-signal-darker" data-id="element-745" data-mp="3fcicqbpv6zkj">
                START A CAMPAIGN
                <ArrowRightIcon size={17} aria-hidden="true" data-id="element-746" data-mp="3t7ujjhhaoght" />
              </Link>
              <Link to="/platform" className={`inline-flex min-h-14 items-center justify-center rounded-full border px-7 text-sm font-extrabold transition-colors hover:bg-white/10 ${product.theme.hairline}`} data-id="element-747" data-mp="20hygrdajiwm">
                SEE THE PLATFORM
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div style={reduceMotion ? undefined : {
        y: panelY
      }} className={`border p-6 md:p-7 ${product.theme.panel}`} data-id="element-748" data-mp="312wm2rgr7ov">
          <p className={`text-xs font-extrabold tracking-[0.14em] ${product.theme.onSurfaceMuted}`} data-id="element-749" data-mp="c0g9z6ipkgev">
            CAMPAIGN FLOW
          </p>
          <ol className="mt-7 flex flex-col gap-4" data-id="element-750" data-mp="3ghtvovapvf7y">
            {product.steps.map((step, index) => <li key={step.title} className={`flex items-baseline gap-3 border-b pb-4 last:border-b-0 last:pb-0 ${product.theme.hairline}`} data-id="element-751" data-mp="3biz2r0j1caa4" data-mpi={index}>
                <span className={`font-display text-sm font-bold ${index === 0 ? 'text-signal' : product.theme.onSurfaceMuted}`} data-id="element-752" data-mp="3khb1xqxywge6">
                  0{index + 1}
                </span>
                <span className="text-sm font-extrabold tracking-[0.1em]" data-id="element-753" data-mp="2sqog8h8ouoy6">
                  {step.title}
                </span>
              </li>)}
          </ol>
        </motion.div>
      </div>
    </section>;
}